import React, { useState } from "react";
import { X } from "lucide-react";
import ExpirationTimer from "../../authScreens/ForgotPassword/ExpirationTimer";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "../../@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { OTP_TIME_MILLISEC } from "../../lib/constants";
import {
  useDecodedTokenResult,
  validateField,
  isMobileScreen,
} from "../../lib/utils";
import { useCommonVerifyOtpMutation, useSendOtpMutation } from "../../api/api";
import SortupDialog from "../../components/common/SortupDialog";

interface OtpDialogProps {
  isOTPDialogOpen: boolean;
  setIsOTPDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOtpVerified: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenPayoutDialog: React.Dispatch<React.SetStateAction<boolean>>;
  timeLeft: number;
  setTimeLeft: React.Dispatch<React.SetStateAction<number>>;
}

const OtpDialog: React.FC<OtpDialogProps> = ({
  isOTPDialogOpen,
  setIsOTPDialogOpen,
  setIsOtpVerified,
  setOpenPayoutDialog,
  timeLeft,
  setTimeLeft,
}) => {
  const [errors, setError] = useState<any>({});
  const [value, setValue] = useState("");
  const { email } = useDecodedTokenResult();
  const [onSendOtp, { isLoading: isResendingOtp }] = useSendOtpMutation();
  const [verifyOtpApi, { isLoading: isVerifyingOtp }] =
    useCommonVerifyOtpMutation();

  const handleOtpChange = (newOtp: string) => {
    setError({});
    const otpError = validateField("otp", newOtp, { required: true });
    if (otpError) {
      setError({ otp: otpError });
      return;
    }
    setValue(newOtp);
  };

  const handleVerifyOtp = async () => {
    setError({});
    const otpError = validateField("otp", value, { required: true });
    if (otpError) {
      setError({ otp: otpError });
      return;
    }
    try {
      const response = await verifyOtpApi({
        body: {
          emailOrPhone: email,
          role: "ADMIN",
          otp: parseInt(value),
        },
      }).unwrap();
      if (response.token) {
        setIsOtpVerified(true);
        setIsOTPDialogOpen(false);
        setValue("");
        setOpenPayoutDialog(true);
      }
    } catch (error) {
      setError({ otp: "Invalid OTP" });
    }
  };

  const handleResendOtp = async () => {
    setError({});
    try {
      await onSendOtp({
        body: { emailOrPhone: email, role: "ADMIN", isSimpleOTP: false },
      }).unwrap();
      setTimeLeft(OTP_TIME_MILLISEC);
    } catch (error) {
      setError({ otp: "Failed to resend OTP" });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !errors.otp && value) {
      handleVerifyOtp();
    }
  };

  const otpMessage =
    "For your security, an OTP has been sent to your registered email. Please enter it below promptly, as it will expire if this popup is closed or navigated away from.";

  const dialogContent = (
    <div className="max-h-[32rem] overflow-y-auto">
      {/* Fixed OTP Destination Message */}
      <div className="mb-4 text-center">
        <p className="text-gray-700 text-sm">{otpMessage}</p>
      </div>

      {/* Expiration Timer */}
      <div className="mb-4">
        <ExpirationTimer
          key={timeLeft}
          expiryTime={OTP_TIME_MILLISEC}
          setTimeLeft={setTimeLeft}
          timeLeft={timeLeft}
        />
      </div>

      {/* OTP Input */}
      <div className="mb-4 overflow-hidden p-1">
        <InputOTP
          required
          maxLength={6}
          pattern={REGEXP_ONLY_DIGITS}
          onChange={handleOtpChange}
          onKeyDown={handleKeyDown}
          className={`${
            errors.otp ? "border-red-500" : "border-gray-300"
          } -mt-2 col-span-1 gap-x-1`}
        >
          <InputOTPGroup>
            <InputOTPSlot index={0} />
            <InputOTPSeparator />
            <InputOTPSlot index={1} />
            <InputOTPSeparator />
            <InputOTPSlot index={2} />
            <InputOTPSeparator />
            <InputOTPSlot index={3} />
            <InputOTPSeparator />
            <InputOTPSlot index={4} />
            <InputOTPSeparator />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>

        {errors.otp && <p className="text-red-500 text-sm mt-2">Invalid OTP</p>}
      </div>

      {/* Resend OTP Button */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleResendOtp}
          className={`text-customGrayBase text-[1.8vh] font-helveticaMedium ${
            isResendingOtp || timeLeft > 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
          disabled={isResendingOtp || timeLeft > 0}
        >
          I didn't receive the code!
          <span
            className={`${
              isResendingOtp || timeLeft > 0
                ? "text-customGreenDisabled"
                : "text-customGreenColor"
            }`}
          >
            {" "}
            Resend code
          </span>
        </button>
      </div>
    </div>
  );

  const footerButtons = [
    {
      name: "Verify OTP",
      onClick: handleVerifyOtp,
      disabled: isVerifyingOtp || !value || errors.otp,
      className: `${
        isVerifyingOtp || !value || errors.otp
          ? "opacity-50 cursor-not-allowed text-white"
          : "text-white"
      } mx-2 w-full`,
    },
  ];

  return (
    <SortupDialog
      isOpen={isOTPDialogOpen}
      onClose={() => setIsOTPDialogOpen(false)}
      header="OTP Verification"
      footerButtons={footerButtons}
      bodyClassName={`p-6 pt-5 px-4 ${isMobileScreen ? "w-full" : "w-[36vw]"}`}
    >
      {dialogContent}
    </SortupDialog>
  );
};

export default OtpDialog;
